import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { AuthService, supabase } from "../services/authService";
import { clearReadingPositionsForUser } from "../services/readingPosition";
import { clearReturnTarget } from "../routing/appRoutes";

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  level: string;
  interests: string[];
  targetTier: string;
  xp: number;
  streakDays: number;
}

interface UserContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  login: (userId: string, userProfile: UserProfile) => void;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  updateUser: (updates: Partial<UserProfile>) => void;
  addXP: (amount: number) => void;
  incrementStreak: () => void;
  resetStreak: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const sessionRestoreGeneration = useRef(0);
  const hasAuthenticatedUser = useRef(false);

  const login = (userId: string, userProfile: UserProfile) => {
    hasAuthenticatedUser.current = true;
    setUser(userProfile);
    setIsAuthLoading(false);
    // Store user data in localStorage for persistence
    localStorage.setItem("user", JSON.stringify(userProfile));
  };

  const clearStoredUser = () => {
    sessionRestoreGeneration.current += 1;
    hasAuthenticatedUser.current = false;
    setUser(null);
    setIsAuthLoading(false);
    localStorage.removeItem("user");
    localStorage.removeItem("onboardingUserProfile");
    clearReturnTarget();
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearStoredUser();
    }
  };

  const deleteAccount = async () => {
    const deletedUserId = user?.id;
    await AuthService.deleteAccount();

    if (deletedUserId) {
      clearReadingPositionsForUser(deletedUserId);
    }

    clearStoredUser();
  };

  const updateUser = (updates: Partial<UserProfile>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const addXP = (amount: number) => {
    if (user) {
      const newXP = user.xp + amount;
      updateUser({ xp: newXP });

      // Check for level up (every 1000 XP = 1 level)
      const currentLevel = Math.floor(user.xp / 1000) + 1;
      const newLevel = Math.floor(newXP / 1000) + 1;

      if (newLevel > currentLevel) {
        // Level up!
        const levelNames = ["A1", "A2", "B1", "B2", "C1", "C2"];
        const newLevelName =
          levelNames[Math.min(newLevel - 1, levelNames.length - 1)];
        updateUser({ level: newLevelName, targetTier: newLevelName });
      }
    }
  };

  const incrementStreak = () => {
    if (user) {
      updateUser({ streakDays: user.streakDays + 1 });
    }
  };

  const resetStreak = () => {
    if (user) {
      updateUser({ streakDays: 0 });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const persistUser = (
      userProfile: UserProfile | null,
      restoreGeneration: number,
    ) => {
      if (
        !isMounted ||
        restoreGeneration !== sessionRestoreGeneration.current
      ) {
        return;
      }

      hasAuthenticatedUser.current = Boolean(userProfile);
      setUser(userProfile);
      setIsAuthLoading(false);
      if (userProfile) {
        localStorage.setItem("user", JSON.stringify(userProfile));
      } else {
        localStorage.removeItem("user");
      }
    };

    const restoreSession = async () => {
      sessionRestoreGeneration.current += 1;
      const restoreGeneration = sessionRestoreGeneration.current;

      try {
        const userProfile = await AuthService.getCurrentUserProfile();
        persistUser(userProfile, restoreGeneration);
      } catch (error) {
        if (
          !isMounted ||
          restoreGeneration !== sessionRestoreGeneration.current
        ) {
          return;
        }

        console.error("Error restoring Supabase session:", error);
        persistUser(null, restoreGeneration);
      }
    };

    restoreSession();

    const authStateListener = supabase.auth.onAuthStateChange(
      (event: string) => {
        if (event === "SIGNED_OUT") {
          clearStoredUser();
          return;
        }

        if (event === "TOKEN_REFRESHED") {
          restoreSession();
          return;
        }

        if (event === "SIGNED_IN") {
          if (isMounted && !hasAuthenticatedUser.current) {
            setIsAuthLoading(true);
          }
          restoreSession();
        }
      },
    );

    return () => {
      isMounted = false;
      authStateListener?.data?.subscription?.unsubscribe();
    };
  }, []);

  const value: UserContextType = {
    user,
    isAuthenticated: !!user,
    isAuthLoading,
    login,
    logout,
    deleteAccount,
    updateUser,
    addXP,
    incrementStreak,
    resetStreak,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
